/**
 * Pure scoring helpers for the "warn on duplicate reimbursement" feature.
 *
 * How the pipeline fits together:
 *   1. `useDuplicateExpenseCheck` runs `GET_TRANSACTIONS_FOR_DUPLICATE_CHECK`
 *      for the same payTo + same type over the last 30d, narrowed to amounts
 *      within ±$1 via
 *      `buildAmountSearchTerm` (uses the `min:X max:Y` parsers in
 *      shared/src/dModels/utils/searchQuery.js).
 *   2. The hook hands those candidates to `rankDuplicateCandidates` along with
 *      the in-progress form values.
 *   3. We score each candidate here, drop non-matches, and return them sorted
 *      by score (highest first).
 *
 */

import TransactionsType from "enums/TransactionsType";
import {
  CashExpense,
  FormikCreateExpense,
  MileageExpense,
} from "./reimbursementExpenseFormat";
import { FormikAddress } from "utils/addressFormat";

// ---------------------------------------------------------------------------
// Tunables — change these to adjust matching strictness.
//
//   AMOUNT TOLERANCE: max absolute dollar diff to count as a match
//     (1 = ±$1.00). The amount value is also fed to buildAmountSearchTerm,
//     so the server-side narrowing matches the client-side check — keep
//     them in sync.
//
//   DISTANCE TOLERANCE: max fractional diff to count as a match
//     (0.02 = ±2%) for mileage miles.
//
//   DATE WINDOW DAYS: how many days apart the form's date and the
//     candidate's transactionDate can be and still count as "date close".
//     Both are currently submission timestamps (the form defaults to
//     `new Date()`), so this effectively flags candidates submitted within
//     the last day. Tight on purpose: same merchant + same amount + same-day
//     submission is the classic "user clicked submit twice" pattern. If we
//     later capture an actual transaction date from the user, this window
//     should still work since it'd compare like-to-like.
//
//   GEOCODE DISTANCE METERS: two geocodes within this distance count as
//     the same location. Primary mileage route signal; falls back to
//     address-string equality when geocodes aren't usable.
// ---------------------------------------------------------------------------
export const DUPLICATE_AMOUNT_TOLERANCE = 1;
export const DUPLICATE_DISTANCE_TOLERANCE = 0.02;
export const DUPLICATE_DATE_WINDOW_DAYS = 1;
export const DUPLICATE_GEOCODE_DISTANCE_METERS = 500;

// Per-signal "must match or reject" toggles for the secondary signals. Flip
// any of these to true to require that signal for a candidate to be considered
// a duplicate at all. When false the signal is still scored (bonus points),
// it just can't reject a candidate on its own.
export const REQUIRE_DATE_MATCH = true;
export const REQUIRE_MERCHANT_MATCH = true;
export const REQUIRE_ROUTE_MATCH = false;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface DuplicateCandidate {
  id: string;
  type: TransactionsType;
  transactionDate: number;
  amount: number;
  currency?: string;
  status?: string;
  merchant?: string | null;
  memo?: string | null;
  description?: string | null;
  merchantData?: {
    name?: string | null;
    merchantId?: string | null;
    logo?: string | null;
    cachedLogo?: string | null;
    url?: string | null;
  } | null;
  mileage?: {
    miles?: number | null;
    isRoundTrip?: boolean | null;
    tripFrom?: Partial<FormikAddress> | null;
    tripTo?: Partial<FormikAddress> | null;
  } | null;
  user?: {
    id?: string;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
}

export interface ScoredDuplicate {
  candidate: DuplicateCandidate;
  score: number;
  reasons: string[];
}

const normalize = (value?: string | null) =>
  (value ?? "").trim().toLowerCase().replace(/\s+/g, " ");

const absAmount = (n: number) => Math.abs(n || 0);

const withinPct = (a: number, b: number, pct: number) => {
  const aa = absAmount(a);
  const bb = absAmount(b);
  if (aa === 0 || bb === 0) return aa === bb;
  const diff = Math.abs(aa - bb) / Math.max(aa, bb);
  return diff <= pct;
};

const withinDollars = (a: number, b: number, dollars: number) =>
  Math.abs(absAmount(a) - absAmount(b)) <= dollars;

const daysBetween = (
  a: number | Date | null | undefined,
  b: number | Date | null | undefined,
): number | null => {
  if (a == null || b == null) return null;
  const at = a instanceof Date ? a.getTime() : a;
  const bt = b instanceof Date ? b.getTime() : b;
  return Math.abs(at - bt) / DAY_MS;
};

const haversineMeters = (
  a: { x?: number | null; y?: number | null } | null | undefined,
  b: { x?: number | null; y?: number | null } | null | undefined,
): number | null => {
  if (!a || !b) return null;
  if (a.x == null || a.y == null || b.x == null || b.y == null) return null;
  if (a.x === 0 && a.y === 0) return null;
  if (b.x === 0 && b.y === 0) return null;

  const R = 6_371_000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const lat1 = toRad(a.y);
  const lat2 = toRad(b.y);
  const dLat = toRad(b.y - a.y);
  const dLon = toRad(b.x - a.x);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

const addressesMatch = (
  a?: Partial<FormikAddress> | null,
  b?: Partial<FormikAddress> | null,
) => {
  if (!a || !b) return false;

  const geoDist = haversineMeters(a.geocode, b.geocode);
  if (geoDist != null && geoDist <= DUPLICATE_GEOCODE_DISTANCE_METERS) {
    return true;
  }

  const aStr = normalize(
    [a.address1, a.city, a.state, a.zipcode].filter(Boolean).join(" "),
  );
  const bStr = normalize(
    [b.address1, b.city, b.state, b.zipcode].filter(Boolean).join(" "),
  );
  if (!aStr || !bStr) return false;
  return aStr === bStr;
};

const merchantsMatch = (
  formMerchantName?: string | null,
  candidate?: DuplicateCandidate,
) => {
  const formName = normalize(formMerchantName);
  if (!formName) return false;

  const candidateName = normalize(
    candidate?.merchantData?.name ?? candidate?.merchant,
  );
  if (!candidateName) return false;

  if (formName === candidateName) return true;
  if (candidateName.includes(formName) || formName.includes(candidateName)) {
    return true;
  }
  return false;
};

// Score a candidate against a CASH expense. Returns null if it's not a
// plausible duplicate.
//
// Gating (must pass to be a candidate at all):
//   - any signal with its REQUIRE_*_MATCH flag set to true must match
//   - AND at least one secondary signal (date OR merchant) must match
//     (amount-only is too weak to flag: same person can spend $20 at
//     different places in the same week)
//
export const scoreCashCandidate = (
  expense: CashExpense,
  candidate: DuplicateCandidate,
): ScoredDuplicate | null => {
  if (candidate.type !== TransactionsType.CASH) return null;

  const amountClose = withinDollars(
    expense.amount,
    candidate.amount,
    DUPLICATE_AMOUNT_TOLERANCE,
  );
  if (!amountClose) return null;

  const days = daysBetween(expense.date, candidate.transactionDate);
  const dateClose = days != null && days <= DUPLICATE_DATE_WINDOW_DAYS;
  const merchantClose = merchantsMatch(expense.merchant?.name, candidate);

  if (REQUIRE_DATE_MATCH && !dateClose) return null;
  if (REQUIRE_MERCHANT_MATCH && !merchantClose) return null;
  if (!dateClose && !merchantClose) return null;

  let score = 50;
  const reasons: string[] = ["duplicateReasonAmountClose"];
  if (merchantClose) {
    score += 30;
    reasons.push("duplicateReasonSameMerchant");
  }
  if (dateClose && days != null) {
    score += Math.max(1, 5 - Math.round(days));
    reasons.push(
      days < 1 ? "duplicateReasonSameDay" : "duplicateReasonDateClose",
    );
  }

  return { candidate, score, reasons };
};

// Same shape as scoreCashCandidate, but the strong signal is "same route"
// (geocode or address match on both endpoints) instead of merchant. Adds a
// small +5 bonus when isRoundTrip matches as a tiebreaker.
export const scoreMileageCandidate = (
  expense: MileageExpense,
  candidate: DuplicateCandidate,
): ScoredDuplicate | null => {
  if (candidate.type !== TransactionsType.MILEAGE) return null;

  const distanceClose = withinPct(
    expense.distance,
    candidate.mileage?.miles ?? 0,
    DUPLICATE_DISTANCE_TOLERANCE,
  );
  if (!distanceClose) return null;

  const days = daysBetween(expense.date, candidate.transactionDate);
  const dateClose = days != null && days <= DUPLICATE_DATE_WINDOW_DAYS;
  const routeClose =
    addressesMatch(expense.tripFrom, candidate.mileage?.tripFrom) &&
    addressesMatch(expense.tripTo, candidate.mileage?.tripTo);

  if (REQUIRE_DATE_MATCH && !dateClose) return null;
  if (REQUIRE_ROUTE_MATCH && !routeClose) return null;
  if (!dateClose && !routeClose) return null;

  let score = 50;
  const reasons: string[] = ["duplicateReasonDistanceClose"];
  if (routeClose) {
    score += 30;
    reasons.push("duplicateReasonSameRoute");
  }
  if (dateClose && days != null) {
    score += Math.max(1, 5 - Math.round(days));
    reasons.push(
      days < 1 ? "duplicateReasonSameDay" : "duplicateReasonDateClose",
    );
  }
  if (
    expense.isRoundTrip != null &&
    candidate.mileage?.isRoundTrip != null &&
    expense.isRoundTrip === candidate.mileage.isRoundTrip
  ) {
    score += 5;
  }

  return { candidate, score, reasons };
};

const isCashExpense = (expense: FormikCreateExpense): expense is CashExpense =>
  expense.expenseType === TransactionsType.CASH;

const isMileageExpense = (
  expense: FormikCreateExpense,
): expense is MileageExpense =>
  expense.expenseType === TransactionsType.MILEAGE;

export const rankDuplicateCandidates = (
  expense: FormikCreateExpense,
  candidates: DuplicateCandidate[],
  ignoreId?: string,
): ScoredDuplicate[] => {
  const filtered = candidates.filter((c) => c.id && c.id !== ignoreId);

  const scored: ScoredDuplicate[] = [];
  if (isCashExpense(expense)) {
    for (const candidate of filtered) {
      const s = scoreCashCandidate(expense, candidate);
      if (s) scored.push(s);
    }
  } else if (isMileageExpense(expense)) {
    for (const candidate of filtered) {
      const s = scoreMileageCandidate(expense, candidate);
      if (s) scored.push(s);
    }
  }

  return scored.sort((a, b) => b.score - a.score);
};

export const buildAmountSearchTerm = (
  amount: number,
  tolerance = DUPLICATE_AMOUNT_TOLERANCE,
): string | undefined => {
  const a = absAmount(amount);
  if (!a) return undefined;
  const min = Math.max(0, a - tolerance).toFixed(2);
  const max = (a + tolerance).toFixed(2);
  return `min:${min} max:${max}`;
};
